<template>
    <feature ref="feature" feature-key="dashboard" sub-key="page">
        <main
            class="flex-grow w-full">
            <div class="flex mb-32">
                <avatar :image="currentUser.image_url"
                        class="mr-16">
                    {{ currentUser.initials }}
                </avatar>

                <div>
                    <h1 class="block text-heading-1 text-primary-80 leading-none">
                        {{ welcomeMessage }},
                        <strong class="text-primary">{{ currentUser.first_name }}</strong>
                    </h1>
                    <p class="mt-8 block text-primary-80">{{ $t('general.welcome_info') }}</p>
                </div>
            </div>

            <div class="2xl:flex 2xl:space-x-32 items-stretch">
                <div class="mb-32 2xl:mb-0 flex-1 grow">

                    <candidate-overview v-if="can.applications" class="grid grid-cols-12 gap-32"></candidate-overview>

                    <div class="grid grid-cols-1 gap-32 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 mt-32 h-auto">
                        <candidate-lists v-if="can.applications"
                                         class="col-span-3 lg:col-span-2"/>

                        <activity class="col-span-3 lg:col-span-2"></activity>
                    </div>
                </div>

                <div
                    v-if="currentUserIsAdmin"
                    class="lg:min-w-0 flex justify-start 2xl:flex-col 2xl:max-w-400 space-x-32 2xl:space-x-0"
                >
                    <calendar-agenda class="grow flex 2xl:flex-col h-420 2xl:h-564"/>
                    <summary-overview class="order-last 2xl:order-first 2xl:mb-32"></summary-overview>
                </div>
            </div>

            <language-picker
                v-if="showLanguageModal"
                :user="currentUser"
                @saved="showLanguageModal = false"
            ></language-picker>
        </main>
    </feature>
</template>

<script>
import Feature from '../components/Feature.vue';
import Avatar from '../components/ui/Avatar.vue';
import CalendarAgenda from './dashboard/CalendarAgenda.vue';
import SummaryOverview from './dashboard/SummaryOverview.vue';
import CandidateOverview from './dashboard/CandidateOverview.vue';
import CandidateLists from './dashboard/CandidateLists.vue';
import * as dashboardService from '../services/DashboardService';
import moment from 'moment';
import SimpleButton from "../components/ui/SimpleButton";
import SvgIcon from '../components/ui/SvgIcon.vue'
import Tooltip from "../components/navigation/Tooltip";
import LanguagePicker from "../components/LanguagePicker.vue";
import events from "../mixins/calendar/dashboard/events";
import SimpleDropdown from "../components/ui/SimpleDropdown.vue";
import Activity from "./dashboard/Activity.vue";
import {mapStores} from "pinia";
import {useMainStore} from "../state/store";
import {useDashboardStore} from "../state/modules/dashboard";

export default {
    name: 'Dashboard',
    components: {
        SimpleDropdown,
        LanguagePicker,
        Feature,
        CandidateLists,
        CandidateOverview,
        SummaryOverview,
        CalendarAgenda,
        'avatar': Avatar,
        moment,
        SimpleButton,
        SvgIcon,
        Tooltip,
        Activity
    },
    data: function () {
        return {
            showLanguageModal: false,
            prevRouteIsLogin: false,
        };
    },
    mixins: [events],
    computed: {
        ...mapStores(useMainStore, useDashboardStore),
        currentUser() {
            return this.mainStore.currentUser;
        },
        locale() {
            return this.mainStore.locale;
        },
        currentUserIsAdmin() {
            return this.mainStore.currentCompanyUser?.is_owner;
        },
        welcomeMessage() {
            const currentHour = moment.utc().local().locale(this.locale).hour();

            if (currentHour > 16) {
                return this.$t('general.welcome_message_evening');
            }

            if (currentHour > 11) {
                return this.$t('general.welcome_message_afternoon');
            }

            return this.$t('general.welcome_message_morning');
        },
        can() {
            return {
                applications: this.$can([
                    'applications.view',
                    'applications.view.*',
                    'job-applications.view.*',
                ]),
            };
        },
    },
    methods: {
        fetch() {
            let offset = 0;
            let limit = 10;

            // Reset the state back to initial,
            // Since we are working with infinite scroll, the state would keep adding the first results back to the array
            // One solution is to reset the state when coming to this page
            this.dashboardStore.resetState();

            this.dashboardStore.startFetching();

            // Dashboard Applications
            if (this.can.applications) {
                // First we'll fetch the counts
                let count = true;
                dashboardService.applications({offset, limit, count}).then(result => {
                    this.dashboardStore.setCount(result)
                    this.dashboardStore.endFetching('applicationsCount');
                });
                // Then we'll fetch the actual applications
                dashboardService.applications({offset, limit}).then(result => {
                    this.dashboardStore.set(result)
                    this.dashboardStore.endFetching('applications');
                })
            }
            // Dashboard Summary
            if (this.currentUserIsAdmin) {
                dashboardService.summary().then(result => {
                    this.dashboardStore.setSummary(result)
                    this.dashboardStore.endFetching('summary');
                });
            }
        },
    },
    mounted() {
        if (!this.$refs.feature.showFeature) {
            // return this.$router.push('jobs');
        }
        if (this.prevRouteIsLogin && !this.currentUser.communication_language) {
            this.showLanguageModal = true;
        }

        this.fetch();
        // re-fetch every 10 minutes
        window.setInterval(() => {
            this.fetch();
        }, 10 * 60 * 1000)

        this.mainStore.setDocumentTitle(null);
    },
    beforeRouteEnter(to, from, next) {
        if (from.name === 'login') {
            next(vm => vm.prevRouteIsLogin = true);
        } else {
            next();
        }
    },
};
</script>
